import { NAVIGATION_ITEMS, NavigationItem, ROUTES } from "../config/routes";

type PathToolEntry = { path: string; tool: string };

function collectFromNav(
  items: NavigationItem[],
  out: PathToolEntry[]
): void {
  for (const item of items) {
    if (item.path && item.requiredTool) {
      out.push({ path: item.path, tool: item.requiredTool });
    }
    if (item.children?.length) {
      collectFromNav(item.children, out);
    }
  }
}

/**
 * Static fallbacks for routes not present (or not labeled) in NAVIGATION_ITEMS.
 * Longer paths must sort first for longest-prefix matching.
 */
const EXTRA_PATH_TOOLS: PathToolEntry[] = [
  { path: ROUTES.BANNER_SWAP, tool: "BannerSwap" },
  { path: ROUTES.ADMIN_TOOLS, tool: "Admin" },
  { path: ROUTES.BANNER_SWAP_ADMIN_OPS, tool: "Admin" },
  { path: ROUTES.HOMEPAGE, tool: "Home" },
  { path: ROUTES.LOGIN, tool: "Login" },
  { path: ROUTES.ROOT, tool: "Login" },
  { path: ROUTES.SOUND_RECORDINGS_LINKING_TOOL, tool: "SoundRecordingLinkingTool" },
  { path: "/sound_recording_linking_tool", tool: "SoundRecordingLinkingTool" },
];

let cachedEntries: PathToolEntry[] | null = null;

function getPathToolEntries(): PathToolEntry[] {
  if (cachedEntries) return cachedEntries;

  const fromNav: PathToolEntry[] = [];
  collectFromNav(NAVIGATION_ITEMS, fromNav);

  const byPath = new Map<string, string>();
  for (const entry of fromNav) {
    if (!byPath.has(entry.path)) {
      byPath.set(entry.path, entry.tool);
    }
  }
  // Explicit extras override nav (e.g. linking tool entitlement name).
  for (const entry of EXTRA_PATH_TOOLS) {
    byPath.set(entry.path, entry.tool);
  }

  cachedEntries = Array.from(byPath.entries())
    .map(([path, tool]) => ({ path, tool }))
    .sort((a, b) => b.path.length - a.path.length);

  return cachedEntries;
}

/**
 * Resolve a GA `tool_name` from the current pathname using longest-prefix match
 * against nav `requiredTool` values and known route fallbacks.
 */
export function resolveToolName(pathname: string): string {
  const path = pathname.split("?")[0] || "/";

  if (path === "/" || path === ROUTES.LOGIN) return "Login";
  if (path === ROUTES.HOMEPAGE) return "Home";
  if (path === ROUTES.ADMIN_TOOLS || path.startsWith(`${ROUTES.ADMIN_TOOLS}/`)) {
    return "Admin";
  }

  for (const entry of getPathToolEntries()) {
    if (path === entry.path || path.startsWith(`${entry.path}/`)) {
      return entry.tool;
    }
  }

  return "Unknown";
}
